import React, { useEffect, useState } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import * as api from '../lib/api';
import type { HTMLSettings, UserIdentity, Adventure, TripGroup } from '../types';
import { useAuth } from '../lib/auth';

interface Traveler {
  name: string;
  role: string;
  bio: string;
  email: string;
}

interface Props {
  user: UserIdentity;
  onClose: () => void;
  adventures: Adventure[];
  tripGroups: TripGroup[];
  onTripGroupsChange: () => void;
}

export function SettingsPanel({ user, onClose, adventures, tripGroups, onTripGroupsChange }: Props) {
  const { signOut } = useAuth();
  const [tab, setTab] = useState<'general' | 'html' | 'trips' | 'share'>('general');

  // HTML settings — explicit fields so they always render
  const [pageTitle, setPageTitle] = useState('Adventures');
  const [mainHeading, setMainHeading] = useState('My Adventures');
  const [sectionHeader, setSectionHeader] = useState('Welcome to our Adventures!');
  const [sectionDescription, setSectionDescription] = useState('Explore our collection of exciting adventures.');

  // About page fields
  const [aboutStory, setAboutStory] = useState('');
  const [aboutClosing, setAboutClosing] = useState('');
  const [aboutTravelers, setAboutTravelers] = useState<Traveler[]>([]);
  const [aboutCountries, setAboutCountries] = useState('');

  // Search engine indexing
  const [allowRobots, setAllowRobots] = useState(true);

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Transfer code state
  const [transferCode, setTransferCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Change password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMessage, setPwMessage] = useState('');
  const [pwError, setPwError] = useState('');

  // Subdomain state
  const [subdomain, setSubdomain] = useState('');
  const [subdomainInput, setSubdomainInput] = useState('');
  const [subBusy, setSubBusy] = useState(false);
  const [subMessage, setSubMessage] = useState('');
  const [subError, setSubError] = useState('');

  // Trip groups local state
  const [localGroups, setLocalGroups] = useState<TripGroup[]>(tripGroups);
  const [tripSaving, setTripSaving] = useState(false);
  const [tripMessage, setTripMessage] = useState('');

  // Sync when prop changes
  useEffect(() => { setLocalGroups(tripGroups); }, [tripGroups]);

  // Sort adventures by orderId descending (same as sidebar)
  const sortedAdventures = [...adventures].sort((a, b) => b.orderId - a.orderId);

  useEffect(() => {
    (async () => {
      const data = await api.getMetadata();
      if (data?.html_settings) {
        const h = data.html_settings as any;
        if (h.page_title) setPageTitle(h.page_title);
        if (h.main_heading) setMainHeading(h.main_heading);
        if (h.section_header) setSectionHeader(h.section_header);
        if (h.section_description) setSectionDescription(h.section_description);
        if (h.about_story) setAboutStory(h.about_story);
        if (h.about_closing) setAboutClosing(h.about_closing);
        if (Array.isArray(h.about_travelers) && h.about_travelers.length > 0) {
          setAboutTravelers(h.about_travelers);
        }
        if (Array.isArray(h.about_countries) && h.about_countries.length > 0) {
          setAboutCountries(h.about_countries.join(', '));
        }
        if (h.allow_robots !== undefined) setAllowRobots(h.allow_robots);
      }
      if ((data as any)?.subdomain) {
        setSubdomain((data as any).subdomain);
        setSubdomainInput((data as any).subdomain);
      }
    })();
  }, [user.user_id]);

  const saveHTML = async () => {
    setSaving(true);
    setSaveMessage('');

    // Parse countries from comma-separated string
    const countriesList = aboutCountries
      .split(',')
      .map(c => c.trim())
      .filter(c => c.length > 0);

    const settings = {
      page_title: pageTitle,
      main_heading: mainHeading,
      section_header: sectionHeader,
      section_description: sectionDescription,
      about_story: aboutStory,
      about_closing: aboutClosing,
      about_travelers: aboutTravelers,
      about_countries: countriesList,
      allow_robots: allowRobots,
    };

    const ok = await api.updateHTMLSettings(settings);
    setSaving(false);
    setSaveMessage(ok ? '✅ Settings saved!' : '❌ Failed to save');
    if (ok) setTimeout(() => setSaveMessage(''), 3000);
  };

  // Traveler helpers
  const addTraveler = () => {
    setAboutTravelers(prev => [...prev, { name: '', role: 'Traveler', bio: '', email: '' }]);
  };
  const updateTraveler = (index: number, field: keyof Traveler, value: string) => {
    setAboutTravelers(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };
  const removeTraveler = (index: number) => {
    setAboutTravelers(prev => prev.filter((_, i) => i !== index));
  };

  // Trip group helpers
  const addGroup = () => {
    setLocalGroups(prev => [...prev, { name: '', adventure_ids: [], published: false }]);
  };
  const updateGroupName = (index: number, name: string) => {
    setLocalGroups(prev => prev.map((g, i) => i === index ? { ...g, name } : g));
  };
  const toggleGroupAdventure = (index: number, advId: string) => {
    setLocalGroups(prev => prev.map((g, i) => {
      if (i !== index) return g;
      const ids = g.adventure_ids.includes(advId)
        ? g.adventure_ids.filter(id => id !== advId)
        : [...g.adventure_ids, advId];
      return { ...g, adventure_ids: ids };
    }));
  };
  const toggleGroupPublished = (index: number) => {
    setLocalGroups(prev => prev.map((g, i) => i === index ? { ...g, published: !(g.published !== false) } : g));
  };
  const removeGroup = (index: number) => {
    setLocalGroups(prev => prev.filter((_, i) => i !== index));
  };

  const saveTripGroups = async () => {
    const valid = localGroups
      .filter(g => g.name.trim() && g.adventure_ids.length > 0)
      .map(g => ({
        name: g.name,
        adventure_ids: g.adventure_ids,
        ...(g.published === false ? { published: false } : {}),
      }));
    setTripSaving(true);
    setTripMessage('');

    const ok = await api.updateTripGroups(valid);
    setTripSaving(false);
    if (ok) {
      setLocalGroups(valid);
      onTripGroupsChange();
      setTripMessage('✅ Trip groups saved!');
      setTimeout(() => setTripMessage(''), 3000);
    } else {
      setTripMessage('❌ Failed to save');
    }
  };

  // Resolve adventure name from ID
  const advName = (id: string) => {
    const adv = adventures.find(a => a.id === id);
    return adv ? adv.description : id;
  };

  const genCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let c = '';
    for (let i = 0; i < 6; i++) c += chars[Math.floor(Math.random() * chars.length)];
    return `${c.slice(0, 3)}-${c.slice(3)}`;
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const code = genCode();
    const expires = new Date(Date.now() + 3600000).toISOString();
    const ok = await api.createTransferCode(code, expires);
    if (ok) {
      setTransferCode(code);
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    setGenerating(false);
  };

  const handleReset = () => {
    if (window.confirm('Reset all local data and sign out?')) signOut();
  };

  const tabs = ['general', 'html', 'trips', 'share'] as const;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-sand-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-display text-2xl text-sand-900">Settings</h2>
          <button onClick={onClose} className="text-sand-400 hover:text-sand-600 transition-colors">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-sand-200">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors
                ${tab === t ? 'text-adventure border-b-2 border-adventure' : 'text-sand-400 hover:text-sand-600'}`}>
              {t === 'html' ? 'HTML' : t === 'trips' ? 'Trips' : t}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {tab === 'general' && (
            <>
              {/* User info */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Account</h3>
                <div className="card p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-sand-500">User ID</span>
                    <span className="text-sand-800 font-mono text-xs">{user.user_id}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-sand-500">Status</span>
                    <span className={user.is_pro_user ? 'text-green-600' : 'text-amber-600'}>
                      {user.is_pro_user ? '✓ Pro' : 'Free'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Subdomain */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Your Site URL</h3>
                <div className="card p-4 space-y-3">
                  {subdomain && (
                    <a href={`https://${subdomain}.wanderbooks.net`} target="_blank" rel="noreferrer"
                      className="text-adventure text-sm hover:underline block">
                      {subdomain}.wanderbooks.net →
                    </a>
                  )}
                  {subMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 text-sm text-green-700">
                      {subMessage}
                    </div>
                  )}
                  {subError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-sm text-red-700">
                      {subError}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input className="input text-sm flex-1" placeholder="yourname"
                      value={subdomainInput}
                      onChange={e => setSubdomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30))} />
                    <span className="text-sand-400 text-sm flex-shrink-0">.wanderbooks.net</span>
                  </div>
                  <button
                    disabled={subBusy || !subdomainInput || subdomainInput.length < 3 || subdomainInput === subdomain}
                    className="btn-primary w-full justify-center text-sm"
                    onClick={async () => {
                      setSubBusy(true); setSubError(''); setSubMessage('');
                      const result = await api.setSubdomain(subdomainInput);
                      if (result.ok && result.data.success) {
                        setSubdomain(subdomainInput);
                        setSubMessage(`Site URL set to ${subdomainInput}.wanderbooks.net — click Publish to make it live.`);
                      } else {
                        setSubError((result.data as any)?.message || 'Failed to set subdomain');
                      }
                      setSubBusy(false);
                    }}
                  >
                    {subBusy ? 'Saving...' : subdomain ? 'Update URL' : 'Set URL'}
                  </button>
                  <p className="text-xs text-sand-400">
                    Letters, numbers, and hyphens. 3-30 characters. Click Publish after setting to make your site live.
                  </p>
                </div>
              </div>

              {/* Change password */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Password</h3>
                <div className="card p-4 space-y-3">
                  {pwMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 text-sm text-green-700">
                      {pwMessage}
                    </div>
                  )}
                  {pwError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-sm text-red-700">
                      {pwError}
                    </div>
                  )}
                  <input className="input text-sm" type="password" placeholder="Current password"
                    value={oldPassword} onChange={e => setOldPassword(e.target.value)} />
                  <input className="input text-sm" type="password" placeholder="New password (min 8 characters)"
                    value={newPw} onChange={e => setNewPw(e.target.value)} />
                  <input className="input text-sm" type="password" placeholder="Confirm new password"
                    value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
                  <button
                    disabled={pwBusy || !oldPassword || newPw.length < 8 || newPw !== confirmPw}
                    className="btn-primary w-full justify-center text-sm"
                    onClick={async () => {
                      if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
                      setPwBusy(true); setPwError(''); setPwMessage('');
                      try {
                        await updatePassword({ oldPassword, newPassword: newPw });
                        setPwMessage('Password changed successfully!');
                        setOldPassword(''); setNewPw(''); setConfirmPw('');
                      } catch (err: any) {
                        setPwError(err?.message || 'Failed to change password');
                      }
                      setPwBusy(false);
                    }}
                  >
                    {pwBusy ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>

              {/* Danger zone */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Data</h3>
                <button onClick={handleReset} className="btn-danger w-full justify-center text-sm">
                  Reset Local Data & Sign Out
                </button>
              </div>
            </>
          )}

          {tab === 'html' && (
            <div className="space-y-6">
              {/* Save feedback */}
              {saveMessage && (
                <div className={`rounded-lg p-2.5 text-sm ${saveMessage.startsWith('✅')
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'}`}>
                  {saveMessage}
                </div>
              )}

              {/* Main page settings */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Home Page</h3>
                <div>
                  <label className="block text-sm text-sand-600 mb-1">Page Title</label>
                  <input className="input" value={pageTitle}
                    onChange={e => setPageTitle(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-sand-600 mb-1">Main Heading</label>
                  <input className="input" value={mainHeading}
                    onChange={e => setMainHeading(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-sand-600 mb-1">Section Header</label>
                  <input className="input" value={sectionHeader}
                    onChange={e => setSectionHeader(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-sand-600 mb-1">Section Description</label>
                  <input className="input" value={sectionDescription}
                    onChange={e => setSectionDescription(e.target.value)} />
                </div>
              </div>

              {/* About page settings */}
              <div className="space-y-3 border-t border-sand-200 pt-6">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">About Page</h3>

                <div>
                  <label className="block text-sm text-sand-600 mb-1">Your Story</label>
                  <textarea className="input min-h-[100px] resize-y" rows={4}
                    placeholder="Tell visitors about your adventures and what this site means to you..."
                    value={aboutStory}
                    onChange={e => setAboutStory(e.target.value)} />
                </div>

                <div>
                  <label className="block text-sm text-sand-600 mb-1">Closing Note</label>
                  <textarea className="input min-h-[60px] resize-y" rows={2}
                    placeholder="A closing thought for your about page..."
                    value={aboutClosing}
                    onChange={e => setAboutClosing(e.target.value)} />
                </div>
              </div>

              {/* Travelers */}
              <div className="space-y-3 border-t border-sand-200 pt-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Travelers</h3>
                  <button onClick={addTraveler}
                    className="text-xs text-adventure hover:text-adventure-dark transition-colors font-medium">
                    + Add Traveler
                  </button>
                </div>

                {aboutTravelers.length === 0 && (
                  <p className="text-sm text-sand-400 italic">
                    No travelers added yet. A default card will appear on your about page.
                  </p>
                )}

                {aboutTravelers.map((traveler, idx) => (
                  <div key={idx} className="card p-4 space-y-2 relative">
                    <button onClick={() => removeTraveler(idx)}
                      className="absolute top-2 right-2 text-sand-300 hover:text-red-400 transition-colors">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                    <input className="input text-sm" placeholder="Name"
                      value={traveler.name}
                      onChange={e => updateTraveler(idx, 'name', e.target.value)} />
                    <input className="input text-sm" placeholder="Role (e.g. Traveler & Companion)"
                      value={traveler.role}
                      onChange={e => updateTraveler(idx, 'role', e.target.value)} />
                    <textarea className="input text-sm min-h-[60px] resize-y" rows={2}
                      placeholder="Short bio..."
                      value={traveler.bio}
                      onChange={e => updateTraveler(idx, 'bio', e.target.value)} />
                    <input className="input text-sm" placeholder="Email (optional)"
                      value={traveler.email}
                      onChange={e => updateTraveler(idx, 'email', e.target.value)} />
                  </div>
                ))}
              </div>

              {/* Countries */}
              <div className="space-y-3 border-t border-sand-200 pt-6">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Countries Visited</h3>
                <div>
                  <textarea className="input min-h-[80px] resize-y" rows={3}
                    placeholder="Comma-separated list: Australia, Japan, France, Italy..."
                    value={aboutCountries}
                    onChange={e => setAboutCountries(e.target.value)} />
                  <p className="text-xs text-sand-400 mt-1">
                    Separate countries with commas. Leave blank to hide this section.
                  </p>
                </div>
              </div>

              {/* Search engine indexing */}
              <div className="space-y-3 border-t border-sand-200 pt-6">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Search Engines</h3>
                <div className="card p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-sand-800">Allow search engine indexing</p>
                    <p className="text-xs text-sand-400 mt-0.5">
                      {allowRobots ? 'Your site can appear in Google, Bing, etc.' : 'Search engines will be asked not to index your site.'}
                    </p>
                  </div>
                  <button
                    onClick={() => setAllowRobots(prev => !prev)}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${allowRobots ? 'bg-adventure' : 'bg-sand-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${allowRobots ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              {/* Custom about upload info */}
              <div className="border-t border-sand-200 pt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-500 flex-shrink-0 mt-0.5">
                    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                  </svg>
                  <p className="text-xs text-blue-700">
                    These settings generate a default About page. To use a fully custom About page,
                    upload your own <code className="bg-blue-100 px-1 rounded">about.html</code> file — it will
                    override the generated version.
                  </p>
                </div>
              </div>

              <button onClick={saveHTML} disabled={saving} className="btn-primary w-full justify-center">
                {saving ? 'Saving...' : 'Save HTML Settings'}
              </button>
            </div>
          )}

          {tab === 'trips' && (
            <div className="space-y-6">
              {tripMessage && (
                <div className={`rounded-lg p-2.5 text-sm ${tripMessage.startsWith('✅')
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'}`}>
                  {tripMessage}
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Trip Groups</h3>
                  <button onClick={addGroup}
                    className="text-xs text-adventure hover:text-adventure-dark transition-colors font-medium">
                    + Add Group
                  </button>
                </div>

                <p className="text-sm text-sand-400">
                  Group consecutive adventures into named trips. Each group shows a route map on the dashboard and published site.
                </p>

                {localGroups.length === 0 && (
                  <p className="text-sm text-sand-400 italic">
                    No trip groups yet. Adventures will display in a flat list.
                  </p>
                )}

                {localGroups.map((group, idx) => (
                  <div key={idx} className="card p-4 space-y-3 relative">
                    <button onClick={() => removeGroup(idx)}
                      className="absolute top-2 right-2 text-sand-300 hover:text-red-400 transition-colors">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>

                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <label className="block text-xs text-sand-500 mb-1">Group Name</label>
                        <input className="input text-sm" placeholder="e.g. Japan 2024"
                          value={group.name}
                          onChange={e => updateGroupName(idx, e.target.value)} />
                      </div>
                      <div className="flex flex-col items-center gap-1 pt-4 flex-shrink-0">
                        <button
                          onClick={() => toggleGroupPublished(idx)}
                          className={`relative w-9 h-5 rounded-full transition-colors ${group.published !== false ? 'bg-adventure' : 'bg-sand-300'}`}>
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${group.published !== false ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                        <span className={`text-[10px] ${group.published !== false ? 'text-adventure' : 'text-sand-400'}`}>
                          {group.published !== false ? 'Published' : 'Planning'}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-sand-500 mb-1">
                        Adventures ({group.adventure_ids.length} selected)
                      </label>
                      <div className="max-h-40 overflow-y-auto border border-sand-200 rounded-lg divide-y divide-sand-100">
                        {sortedAdventures.map(a => (
                          <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-sand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={group.adventure_ids.includes(a.id)}
                              onChange={() => toggleGroupAdventure(idx, a.id)}
                              className="accent-adventure"
                            />
                            <span className="text-sm text-sand-700 truncate">{a.description}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={saveTripGroups} disabled={tripSaving} className="btn-primary w-full justify-center">
                {tripSaving ? 'Saving...' : 'Save Trip Groups'}
              </button>
            </div>
          )}

          {tab === 'share' && (
            <div className="space-y-6">
              {/* Generate */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Share Your Adventures</h3>
                <p className="text-sm text-sand-500">Generate a code to share with family and friends.</p>

                {!transferCode ? (
                  <button onClick={handleGenerate} disabled={generating} className="btn-primary w-full justify-center">
                    {generating ? 'Generating...' : 'Generate Transfer Code'}
                  </button>
                ) : (
                  <div className="card p-4 text-center space-y-2">
                    <div className="flex items-center justify-center gap-3">
                      <span className="font-mono text-3xl font-bold tracking-widest text-sand-900">{transferCode}</span>
                      <button onClick={() => { navigator.clipboard.writeText(transferCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                        className="text-adventure hover:text-adventure-dark transition-colors">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                      </button>
                    </div>
                    {copied && <span className="text-green-600 text-sm">Copied!</span>}
                    <button onClick={handleGenerate} className="text-sm text-adventure">Generate New Code</button>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-500 flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                  <p className="text-xs text-blue-700">All devices using this code share the same adventures and subscription.</p>
                </div>
              </div>

              {/* Redeem */}
              <div className="space-y-3 border-t border-sand-200 pt-6">
                <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Join Shared Adventures</h3>
                <div className="flex gap-2">
                  <input className="input font-mono text-center uppercase tracking-widest"
                    placeholder="ABC123" value={inputCode} maxLength={6}
                    onChange={e => setInputCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))} />
                  <button disabled={inputCode.length < 6}
                    className="btn-primary flex-shrink-0">Join</button>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500 flex-shrink-0 mt-0.5"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <p className="text-xs text-amber-700">This replaces your current adventures. Existing data will be lost.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
